import { useContext } from 'react';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { PrimaryButton } from 'components/Button';
import { AnchorButton } from 'components/Button/AnchorButton';
import { LegacyTertiaryButton } from 'components/Button/LegacyTertiaryButton';
import { Hide } from 'components/Hide';
import { Link } from 'components/Link';
import { Body } from 'components/Text/Body';
import { BodySmall } from 'components/Text/BodySmall';
import { H2 } from 'components/Text/H2';
import { Tile } from 'components/Tile';
import { ShowModalContainer } from 'containers/ShowModalContainer';
import { AmpContext } from 'utils/AmpContext';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from 'utils/constants/general/listingTypeHumanNameMapTitle';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypePluralHumanNameMap';
import { getStaticImageSrc } from 'utils/getImageSrc';

const AmpLink = styled(Link)`
  font-size: 16px;
`;

type Props = {
  listingUrl: string;
  type: string;
};

export function ListingSignupCta({ listingUrl, type }: Props) {
  const requestIsAmp = useContext(AmpContext);

  return requestIsAmp ? (
    <Box data-qa-id="job-listing-signup-promo">
      <Tile
        gradientBorder
        position="relative"
        display="flex"
        flex="1"
        py={5}
        px={4}
        borderRadius="4px"
      >
        <Box display="flex" justifyContent="center" width={[0, 0, '50%']}>
          <Hide xs sm>
            <img src={getStaticImageSrc('planning.svg')} alt="Illustration" />
          </Hide>
        </Box>
        <Box
          textAlign={['center', 'center', 'left']}
          width={['100%', '100%', '50%']}
        >
          <H2 mb={3}>{getText('Join Idealist')}</H2>
          <Body mb={3}>
            {sprintf(
              getText(
                'Sign up today to save your favorite %(type)s and get email alerts when new ones are posted.',
              ),
              {
                // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                type: LISTING_TYPE_PLURAL_HUMAN_NAME_MAP[type],
              },
            )}
          </Body>
          <Box mb={3}>
            <AnchorButton href="/signup" data-qa-id="listing-promo-signup-link">
              {getText('Get Started')}
            </AnchorButton>
          </Box>

          <AmpLink href="/login" data-qa-id="listing-promo-login-link">
            {getText('Already a user? Log in')}
          </AmpLink>
        </Box>
      </Tile>
    </Box>
  ) : (
    <Box data-qa-id="job-listing-signup-promo">
      <Tile
        gradientBorder
        position="relative"
        display="flex"
        flex="1"
        py={5}
        px={4}
        borderRadius="4px"
      >
        <Box display="flex" justifyContent="center" width={[0, 0, '50%']}>
          <Hide xs sm>
            <img src={getStaticImageSrc('planning.svg')} alt="Illustration" />
          </Hide>
        </Box>
        <Box
          textAlign={['center', 'center', 'left']}
          width={['100%', '100%', '50%']}
        >
          <H2 mb={3}>{getText('Join Idealist')}</H2>
          <Body mb={3}>
            {sprintf(
              getText(
                'Sign up today to save your favorite %(type)s and get email alerts when new ones are posted.',
              ),
              {
                // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                type: LISTING_TYPE_PLURAL_HUMAN_NAME_MAP[type],
              },
            )}
          </Body>
          <ShowModalContainer
            render={(showModal) => (
              <>
                <Box mb={3}>
                  <PrimaryButton
                    onClick={(e) => {
                      e.preventDefault();
                      trackClicked(
                        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                        `${LISTING_TYPE_HUMAN_NAME_MAP_TITLE[type]} Listing CTA: Sign Up`,
                      );
                      showModal('SIGNUP', {
                        redirectTo: listingUrl,
                      });
                    }}
                    data-qa-id="listing-promo-signup-link"
                  >
                    {getText('Get Started')}
                  </PrimaryButton>
                </Box>

                <LegacyTertiaryButton
                  onClick={() => {
                    trackClicked(
                      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
                      `${LISTING_TYPE_HUMAN_NAME_MAP_TITLE[type]} Listing CTA: Log In`,
                    );
                    showModal('LOGIN', {
                      redirectTo: listingUrl,
                    });
                  }}
                  data-qa-id="listing-promo-login-link"
                >
                  <BodySmall styleAs="h5">
                    {getText('Already a user? Log in')}
                  </BodySmall>
                </LegacyTertiaryButton>
              </>
            )}
          />
        </Box>
      </Tile>
    </Box>
  );
}
